import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Settings, User, Mail, Lock, Phone, Loader } from 'lucide-react';
import toast from 'react-hot-toast';

import { apiService } from '../services/api';

function InitialSetup({ onSetupComplete }) {
    const { t, i18n } = useTranslation();
    const [formData, setFormData] = useState({
        email: '',
        fullName: '',
        password: '',
        confirmPassword: '',
        phone: ''
    });
    const [loading, setLoading] = useState(false);

    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (formData.password !== formData.confirmPassword) {
            toast.error(t('auth.passwordMismatch', 'Passwords do not match'));
            return;
        }

        if (formData.password.length < 6) {
            toast.error(t('auth.passwordTooShort', 'Password must be at least 6 characters'));
            return;
        }

        setLoading(true);

        try {
            const { confirmPassword, ...payload } = formData;
            const response = await apiService.initialSetup({
                ...payload,
                preferredLanguage: i18n.language
            });
            toast.success(t('setup.setupComplete', 'Initial setup completed successfully!'));
            onSetupComplete(response.user, response.token);
        } catch (error) {
            const message = error.response?.data?.error || t('setup.setupError', 'Setup failed');
            toast.error(message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
            <div className="max-w-md w-full space-y-8">
                <div>
                    <div className="mx-auto h-12 w-12 flex items-center justify-center rounded-full bg-blue-100">
                        <Settings className="h-6 w-6 text-blue-600" />
                    </div>
                    <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
                        {t('setup.welcome', 'Welcome to IoT Monitoring')}
                    </h2>
                    <p className="mt-2 text-center text-sm text-gray-600">
                        {t('setup.createAdmin', 'Create your administrator account to get started')}
                    </p>
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="flex">
                        <div className="flex-shrink-0">
                            <Settings className="h-5 w-5 text-blue-400" />
                        </div>
                        <div className="ml-3">
                            <h3 className="text-sm font-medium text-blue-800">
                                {t('setup.initialSetupTitle', 'Initial Setup')}
                            </h3>
                            <div className="mt-2 text-sm text-blue-700">
                                <p>
                                    {t('setup.initialSetupDescription',
                                        'This is the first-time setup. You are creating the initial administrator account that will have full access to the system.'
                                    )}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
                    <div className="space-y-4">
                        <div>
                            <label htmlFor="fullName" className="block text-sm font-medium text-gray-700">
                                {t('auth.fullName', 'Full Name')}
                            </label>
                            <div className="relative mt-1">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <User className="h-5 w-5 text-gray-400" />
                                </div>
                                <input
                                    id="fullName"
                                    name="fullName"
                                    type="text"
                                    required
                                    className="block w-full px-3 py-2 pl-10 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                    placeholder={t('setup.fullNamePlaceholder', 'Enter your full name')}
                                    value={formData.fullName}
                                    onChange={handleChange}
                                />
                            </div>
                        </div>

                        <div>
                            <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                                {t('auth.email', 'Email Address')}
                            </label>
                            <div className="relative mt-1">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <Mail className="h-5 w-5 text-gray-400" />
                                </div>
                                <input
                                    id="email"
                                    name="email"
                                    type="email"
                                    required
                                    className="block w-full px-3 py-2 pl-10 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                    placeholder={t('setup.emailPlaceholder', 'Enter your email address')}
                                    value={formData.email}
                                    onChange={handleChange}
                                />
                            </div>
                        </div>

                        <div>
                            <label htmlFor="phone" className="block text-sm font-medium text-gray-700">
                                {t('auth.phone', 'Phone Number')} ({t('common.optional', 'Optional')})
                            </label>
                            <div className="relative mt-1">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <Phone className="h-5 w-5 text-gray-400" />
                                </div>
                                <input
                                    id="phone"
                                    name="phone"
                                    type="tel"
                                    className="block w-full px-3 py-2 pl-10 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                    placeholder={t('setup.phonePlaceholder', 'Enter your phone number')}
                                    value={formData.phone}
                                    onChange={handleChange}
                                />
                            </div>
                        </div>

                        <div>
                            <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                                {t('auth.password', 'Password')}
                            </label>
                            <div className="relative mt-1">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <Lock className="h-5 w-5 text-gray-400" />
                                </div>
                                <input
                                    id="password"
                                    name="password"
                                    type="password"
                                    required
                                    className="block w-full px-3 py-2 pl-10 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                    placeholder={t('setup.passwordPlaceholder', 'Create a strong password')}
                                    value={formData.password}
                                    onChange={handleChange}
                                />
                            </div>
                            <p className="mt-1 text-xs text-gray-500">
                                {t('setup.passwordRequirement', 'Password must be at least 6 characters long')}
                            </p>
                        </div>

                        <div>
                            <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">
                                {t('auth.confirmPassword', 'Confirm Password')}
                            </label>
                            <div className="relative mt-1">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <Lock className="h-5 w-5 text-gray-400" />
                                </div>
                                <input
                                    id="confirmPassword"
                                    name="confirmPassword"
                                    type="password"
                                    required
                                    className="block w-full px-3 py-2 pl-10 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                    placeholder={t('setup.confirmPasswordPlaceholder', 'Confirm your password')}
                                    value={formData.confirmPassword}
                                    onChange={handleChange}
                                />
                            </div>
                        </div>
                    </div>

                    <div>
                        <button
                            type="submit"
                            disabled={loading}
                            className="group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {loading && <Loader className="h-4 w-4 mr-2 animate-spin" />}
                            {t('setup.createAdminAccount', 'Create Administrator Account')}
                        </button>
                    </div>
                </form>

                <div className="text-center">
                    <p className="text-xs text-gray-500">
                        {t('setup.securityNote',
                            'This account will have full administrative privileges. Keep your credentials secure.'
                        )}
                    </p>
                </div>
            </div>
        </div>
    );
}

export default InitialSetup;
